import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { CheckCircle2, XCircle, Shield, Shirt, MapPin, DollarSign, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyPage() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/verify/${token}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(err => {
        const status = err.response?.status;
        if (status === 403) {
          setError('ACCESS DENIED — Admin access required to scan QR codes.');
        } else if (status === 404) {
          setError('Invalid QR code — this code does not match any student.');
        } else {
          setError(err.response?.data?.detail || 'Verification failed');
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleCheckin = async () => {
    setCheckingIn(true);
    try {
      await axios.post(`${API}/verify/${token}/checkin`, {}, { withCredentials: true });
      setData(d => ({ ...d, checked_in: true, checked_in_at: new Date().toISOString() }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="text-center">
          <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin" strokeWidth={2} style={{ color: '#121212' }} />
          <p className="font-bold text-sm uppercase tracking-wider" style={{ color: '#888' }}>Verifying QR...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="bg-[#FF6B6B]/10 border-[3px] border-[#FF6B6B] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-8 text-center max-w-md w-full">
          <XCircle className="w-16 h-16 mx-auto mb-4" strokeWidth={2} style={{ color: '#FF6B6B' }} />
          <h1 className="text-xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Scan Failed
          </h1>
          <p className="text-sm font-medium mb-6" style={{ color: '#555' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#121212] text-[#F6F1E8] font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-6 py-3"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F6F1E8' }}>
      <div className={`border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-8 max-w-md w-full ${
        data.checked_in ? 'bg-[#6EE7B7]/20' : 'bg-[#EDE3C7]'
      }`}>
        {/* Header */}
        <div className="text-center mb-6">
          {data.checked_in ? (
            <CheckCircle2 className="w-16 h-16 mx-auto mb-3" strokeWidth={2} style={{ color: '#16a34a' }} />
          ) : (
            <Shield className="w-16 h-16 mx-auto mb-3" strokeWidth={2} style={{ color: '#121212' }} />
          )}
          <h1 className="text-2xl font-black uppercase tracking-tight mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {data.checked_in ? 'Checked In ✓' : 'Valid Student'}
          </h1>
          <p className="text-sm font-medium" style={{ color: '#888' }}>
            {data.checked_in
              ? `Checked in at ${new Date(data.checked_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : 'QR code verified — ready for check-in'}
          </p>
        </div>

        {/* Student Info */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between bg-[#F6F1E8] border-[2px] border-[#121212]/20 rounded-xl px-4 py-3">
            <span className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>Student</span>
            <span className="font-bold text-sm">{data.pseudonym}</span>
          </div>
          <div className="flex items-center justify-between bg-[#F6F1E8] border-[2px] border-[#121212]/20 rounded-xl px-4 py-3">
            <span className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>Name</span>
            <span className="font-bold text-sm">{data.student_name}</span>
          </div>
          <div className="flex items-center justify-between bg-[#F6F1E8] border-[2px] border-[#121212]/20 rounded-xl px-4 py-3">
            <span className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>Startup</span>
            <span className="font-bold text-sm">{data.startup_name}</span>
          </div>
          <div className="flex items-center justify-between bg-[#F6F1E8] border-[2px] border-[#121212]/20 rounded-xl px-4 py-3">
            <span className="text-xs font-black uppercase tracking-wider flex items-center gap-1" style={{ color: '#888' }}>
              <Shirt className="w-3.5 h-3.5" strokeWidth={2.5} /> Size
            </span>
            <span className="font-mono font-bold text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{data.tshirt_size}</span>
          </div>
          <div className="flex items-center justify-between bg-[#F6F1E8] border-[2px] border-[#121212]/20 rounded-xl px-4 py-3">
            <span className="text-xs font-black uppercase tracking-wider flex items-center gap-1" style={{ color: '#888' }}>
              <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} /> Location
            </span>
            <span className="font-bold text-sm">{data.in_bengaluru ? 'Bengaluru (Shipping)' : 'Venue Pickup'}</span>
          </div>
          <div className="flex items-center justify-between bg-[#F6F1E8] border-[2px] border-[#121212]/20 rounded-xl px-4 py-3">
            <span className="text-xs font-black uppercase tracking-wider flex items-center gap-1" style={{ color: '#888' }}>
              <DollarSign className="w-3.5 h-3.5" strokeWidth={2.5} /> Price
            </span>
            <span className="font-mono font-bold text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${data.asking_price}</span>
          </div>
        </div>

        {/* Check-in Button */}
        {!data.checked_in && (
          <button
            onClick={handleCheckin}
            disabled={checkingIn}
            className="w-full bg-[#6EE7B7] text-[#121212] font-black uppercase text-lg border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="checkin-btn"
          >
            <CheckCircle2 className="w-5 h-5" strokeWidth={3} />
            {checkingIn ? 'Checking in...' : 'Check In Student'}
          </button>
        )}
      </div>
    </div>
  );
}
